import { create } from 'zustand';
import { temporal } from 'zundo';
import type { FormSchema, FormField, FormStep, ConditionalRule, FormSettings } from '../types/form';

interface BuilderState {
  // Form data
  formId: string | null;
  formName: string;
  formSchema: FormSchema;
  originalSchema: FormSchema | null;

  // UI state
  selectedFieldId: string | null;
  activeStepId: string | null;
  isDragging: boolean;
  hasUnsavedChanges: boolean;

  // Actions
  initializeForm: (id: string, name: string, schema: FormSchema) => void;
  reset: () => void;

  // Field selection
  selectField: (fieldId: string | null) => void;

  // Field operations
  addField: (field: FormField, afterFieldId?: string) => void;
  updateField: (fieldId: string, updates: Partial<FormField>) => void;
  removeField: (fieldId: string) => void;
  reorderFields: (activeId: string, overId: string) => void;
  duplicateField: (fieldId: string) => void;

  // Step operations
  addStep: (step: FormStep) => void;
  updateStep: (stepId: string, updates: Partial<FormStep>) => void;
  removeStep: (stepId: string) => void;
  reorderSteps: (activeId: string, overId: string) => void;
  moveFieldToStep: (fieldId: string, stepId: string) => void;

  // Conditional logic
  addCondition: (rule: ConditionalRule) => void;
  updateCondition: (ruleId: string, updates: Partial<ConditionalRule>) => void;
  removeCondition: (ruleId: string) => void;
  updateConditionalLogic: (rules: ConditionalRule[]) => void;

  // Settings
  updateSettings: (settings: Partial<FormSettings>) => void;

  // Drag state
  setDragging: (isDragging: boolean) => void;

  // Get schema for saving
  getSchema: () => FormSchema;
  markAsSaved: () => void;
}

const initialSchema: FormSchema = {
  version: 1,
  fields: [],
  steps: [],
  settings: {
    submitButtonText: 'Submit',
    successMessage: 'Thank you for your submission!',
    theme: 'light',
    submitActions: [{ type: 'database' }],
  },
  conditionalLogic: [],
};

export const useBuilderStore = create<BuilderState>()(
  temporal(
    (set, get) => ({
  // Initial state
  formId: null,
  formName: '',
  formSchema: { ...initialSchema },
  originalSchema: null,
  selectedFieldId: null,
  activeStepId: null,
  isDragging: false,
  hasUnsavedChanges: false,

  initializeForm: (id, name, schema) =>
    set({
      formId: id,
      formName: name,
      formSchema: schema,
      originalSchema: JSON.parse(JSON.stringify(schema)),
      selectedFieldId: null,
      hasUnsavedChanges: false,
    }),

  reset: () =>
    set({
      formId: null,
      formName: '',
      formSchema: { ...initialSchema },
      originalSchema: null,
      selectedFieldId: null,
      activeStepId: null,
      hasUnsavedChanges: false,
    }),

  selectField: (fieldId) => set({ selectedFieldId: fieldId }),

  addField: (field, afterFieldId) =>
    set((state) => {
      const fields = [...state.formSchema.fields];
      if (afterFieldId) {
        const index = fields.findIndex((f) => f.id === afterFieldId);
        fields.splice(index + 1, 0, field);
      } else {
        fields.push(field);
      }
      return {
        formSchema: { ...state.formSchema, fields },
        selectedFieldId: field.id,
        hasUnsavedChanges: true,
      };
    }),

  updateField: (fieldId, updates) =>
    set((state) => {
      const fields = state.formSchema.fields.map((f) =>
        f.id === fieldId ? { ...f, ...updates } : f
      );
      return {
        formSchema: { ...state.formSchema, fields },
        hasUnsavedChanges: true,
      };
    }),

  removeField: (fieldId) =>
    set((state) => {
      const fields = state.formSchema.fields.filter((f) => f.id !== fieldId);
      const conditionalLogic = state.formSchema.conditionalLogic.filter(
        (r) => r.targetFieldId !== fieldId && !r.conditions.some((c) => c.fieldId === fieldId)
      );
      return {
        formSchema: { ...state.formSchema, fields, conditionalLogic },
        selectedFieldId: state.selectedFieldId === fieldId ? null : state.selectedFieldId,
        hasUnsavedChanges: true,
      };
    }),

  reorderFields: (activeId, overId) =>
    set((state) => {
      const fields = [...state.formSchema.fields];
      const activeIndex = fields.findIndex((f) => f.id === activeId);
      const overIndex = fields.findIndex((f) => f.id === overId);

      if (activeIndex !== -1 && overIndex !== -1) {
        const [removed] = fields.splice(activeIndex, 1);
        fields.splice(overIndex, 0, removed);
      }

      return {
        formSchema: { ...state.formSchema, fields },
        hasUnsavedChanges: true,
      };
    }),

  duplicateField: (fieldId) =>
    set((state) => {
      const field = state.formSchema.fields.find((f) => f.id === fieldId);
      if (!field) return state;

      const newField: FormField = {
        ...JSON.parse(JSON.stringify(field)),
        id: crypto.randomUUID(),
        name: `${field.name}_copy`,
        label: `${field.label} (Copy)`,
      };

      const fields = [...state.formSchema.fields];
      const index = fields.findIndex((f) => f.id === fieldId);
      fields.splice(index + 1, 0, newField);

      return {
        formSchema: { ...state.formSchema, fields },
        selectedFieldId: newField.id,
        hasUnsavedChanges: true,
      };
    }),

  addStep: (step) =>
    set((state) => ({
      formSchema: {
        ...state.formSchema,
        steps: [...(state.formSchema.steps || []), step],
      },
      activeStepId: step.id,
      hasUnsavedChanges: true,
    })),

  updateStep: (stepId, updates) =>
    set((state) => ({
      formSchema: {
        ...state.formSchema,
        steps: (state.formSchema.steps || []).map((s) =>
          s.id === stepId ? { ...s, ...updates } : s
        ),
      },
      hasUnsavedChanges: true,
    })),

  removeStep: (stepId) =>
    set((state) => ({
      formSchema: {
        ...state.formSchema,
        steps: (state.formSchema.steps || []).filter((s) => s.id !== stepId),
      },
      activeStepId: state.activeStepId === stepId ? null : state.activeStepId,
      hasUnsavedChanges: true,
    })),

  reorderSteps: (activeId, overId) =>
    set((state) => {
      const steps = [...(state.formSchema.steps || [])];
      const activeIndex = steps.findIndex((s) => s.id === activeId);
      const overIndex = steps.findIndex((s) => s.id === overId);

      if (activeIndex !== -1 && overIndex !== -1) {
        const [removed] = steps.splice(activeIndex, 1);
        steps.splice(overIndex, 0, removed);
      }

      return {
        formSchema: { ...state.formSchema, steps },
        hasUnsavedChanges: true,
      };
    }),

  moveFieldToStep: (fieldId, stepId) =>
    set((state) => {
      const steps = (state.formSchema.steps || []).map((step) => ({
        ...step,
        fieldIds: step.fieldIds.filter((id) => id !== fieldId),
      }));

      const stepIndex = steps.findIndex((s) => s.id === stepId);
      if (stepIndex !== -1) {
        steps[stepIndex].fieldIds.push(fieldId);
      }

      return {
        formSchema: { ...state.formSchema, steps },
        hasUnsavedChanges: true,
      };
    }),

  addCondition: (rule) =>
    set((state) => ({
      formSchema: {
        ...state.formSchema,
        conditionalLogic: [...state.formSchema.conditionalLogic, rule],
      },
      hasUnsavedChanges: true,
    })),

  updateCondition: (ruleId, updates) =>
    set((state) => ({
      formSchema: {
        ...state.formSchema,
        conditionalLogic: state.formSchema.conditionalLogic.map((r) =>
          r.id === ruleId ? { ...r, ...updates } : r
        ),
      },
      hasUnsavedChanges: true,
    })),

  removeCondition: (ruleId) =>
    set((state) => ({
      formSchema: {
        ...state.formSchema,
        conditionalLogic: state.formSchema.conditionalLogic.filter((r) => r.id !== ruleId),
      },
      hasUnsavedChanges: true,
    })),

  updateConditionalLogic: (rules) =>
    set((state) => ({
      formSchema: {
        ...state.formSchema,
        conditionalLogic: rules,
      },
      hasUnsavedChanges: true,
    })),

  updateSettings: (settings) =>
    set((state) => ({
      formSchema: {
        ...state.formSchema,
        settings: { ...state.formSchema.settings, ...settings },
      },
      hasUnsavedChanges: true,
    })),

  setDragging: (isDragging) => set({ isDragging }),

  getSchema: () => get().formSchema,

  markAsSaved: () =>
    set((state) => ({
      originalSchema: JSON.parse(JSON.stringify(state.formSchema)),
      hasUnsavedChanges: false,
    })),
}),
    {
      // Only track changes to formSchema for undo/redo
      partialize: (state) => ({
        formSchema: state.formSchema,
      }),
      // Limit history to 50 items
      limit: 50,
      // Don't track rapid changes (debounce)
      handleSet: (handleSet) => {
        return (state) => {
          handleSet(state);
        };
      },
    }
  )
);
